"use client";

import { TradeInOption } from "@alba-cars/common-modules";
import parsePhoneNumberFromString, { isValidPhoneNumber } from "libphonenumber-js";
import { z } from "zod";

import { marketingDataSchema } from "./marketingDataSchema";

// Type for validation message translator (e.g. from useTranslations("SellCar.form.validation"))
export type SellCarValidationTranslator = (key: string) => string;

const DEFAULT_MESSAGES = {
  nameRequired: "Name is required",
  phoneRequired: "Phone number is required",
  phoneInvalid: "Please enter a valid phone number",
  makeRequired: "Make is required",
  makeNameRequired: "Make name is required",
  modelRequired: "Model is required",
  modelNameRequired: "Model name is required",
  specificationRequired: "Specification is required",
  specificationNameRequired: "Specification name is required",
  yearRequired: "Year is required",
  mileagePositive: "Mileage must be a positive number",
  tradeInRequired: "Please select a valid trade-in option",
  required: "Required",
};

const getMessages = (t?: SellCarValidationTranslator): typeof DEFAULT_MESSAGES =>
  t
    ? (Object.fromEntries(
        (Object.keys(DEFAULT_MESSAGES) as (keyof typeof DEFAULT_MESSAGES)[]).map((k) => [k, t(k)]),
      ) as typeof DEFAULT_MESSAGES)
    : DEFAULT_MESSAGES;

/** Creates FirstStepSchema with optional translated validation messages */
export const createFirstStepSchema = (t?: SellCarValidationTranslator) => {
  const m = getMessages(t);
  return z.object({
    name: z.string({ required_error: m.nameRequired }).trim().nonempty({ message: m.nameRequired }),
    phone: z
      .string({ required_error: m.phoneRequired })
      .min(8, m.phoneRequired)
      .refine((value) => {
        if (!value) return false;
        const phoneNumber = parsePhoneNumberFromString(value);
        if (!phoneNumber) return false;
        return isValidPhoneNumber(value);
      }, m.phoneInvalid),
    marketing: marketingDataSchema,
  });
};

/** Creates SecondStepSchema with optional translated validation messages */
export const createSecondStepSchema = (t?: SellCarValidationTranslator) => {
  const m = getMessages(t);
  return z.object({
    brand: z.string({ required_error: m.makeRequired }).trim().nonempty({ message: m.makeRequired }),
    brandName: z.string({ required_error: m.makeNameRequired }).nonempty({ message: m.makeNameRequired }),

    model: z
      .union([z.string().trim(), z.null(), z.undefined()])
      .transform((val) => val ?? "")
      .pipe(z.string().nonempty({ message: m.modelRequired })),

    modelName: z.string({ required_error: m.modelNameRequired }).nonempty({ message: m.modelNameRequired }),

    modelVersion: z
      .union([z.string().trim(), z.null()])
      .transform((val) => val || "")
      .optional(),
    modelVersionName: z
      .union([z.string(), z.null()])
      .transform((val) => val || "")
      .optional(),

    specification: z
      .string({ required_error: m.specificationRequired })
      .trim()
      .nonempty({ message: m.specificationRequired }),
    specificationName: z
      .string({ required_error: m.specificationNameRequired })
      .nonempty({ message: m.specificationNameRequired }),

    year: z.string({ required_error: m.yearRequired }).trim().nonempty({ message: m.yearRequired }),
    mileage: z
      .union([z.string({ required_error: m.required }).transform((val) => (val === "" ? 0 : Number(val))), z.number()])
      .refine((val) => val >= 0, { message: m.mileagePositive }),

    tradeIn: z.nativeEnum(TradeInOption, {
      errorMap: () => ({ message: m.tradeInRequired }),
    }),

    description: z.string().trim().optional(),
  });
};

/** Default schemas (English) for backward compatibility */
export const FirstStepSchema = createFirstStepSchema();
export const SecondStepSchema = createSecondStepSchema();

export type FirstStepSchemaType = z.infer<ReturnType<typeof createFirstStepSchema>>;
export type SecondStepSchemaType = z.infer<ReturnType<typeof createSecondStepSchema>>;

export const ThirdStepSchema = z.object({
  images: z.array(z.any()).optional(),
});

// Define MarketingSchema based on MarketingData interface
// export const SellCarMarketingSchema = z.object({
//   marketing: z
//     .object({
//       Meta: z
//         .object({
//           utm_source: z.string(),
//           utm_medium: z.string(),
//           utm_campaign: z.string(),
//           utm_content: z.string(),
//         })
//         .optional(),
//       Google: z
//         .object({
//           gclid: z.string(),
//           utm_source: z.string(),
//           utm_medium: z.string(),
//           utm_campaign: z.string(),
//           utm_term: z.string(),
//           utm_content: z.string(),
//         })
//         .optional(),
//       Bing: z
//         .object({
//           msclkid: z.string(),
//           utm_source: z.string(),
//           utm_medium: z.string(),
//           utm_campaign: z.string(),
//           utm_term: z.string(),
//           utm_content: z.string(),
//         })
//         .optional(),
//     })
//     .optional(),
// });

// export type SellCarMarketingSchemaType = z.infer<typeof SellCarMarketingSchema>;

export const SellCarFormSchema = FirstStepSchema.merge(SecondStepSchema)
  .merge(ThirdStepSchema)
  // .merge(SellCarMarketingSchema)
  .transform((data) => ({
    name: data.name,
    phone: data.phone,

    brand: data.brand,
    brandName: data.brandName,

    model: data.model,
    modelName: data.modelName,

    modelVersion: data.modelVersion,
    modelVersionName: data.modelVersionName,

    specification: data.specification,
    specificationName: data.specificationName,

    year: data.year,
    mileage: Number(data.mileage),
    // expectedSellingPrice: Number(data.expectedSellingPrice),

    tradeIn: data.tradeIn,
    description: data.description,
    images: (data.images?.filter((img: { url?: string }) => img?.url)?.map((img: { url: string }) => img.url) ??
      []) as string[],
    // marketing: data.marketing,
  }));

/** Creates full SellCarFormSchema with optional translated validation messages */
export const createSellCarFormSchema = (t?: SellCarValidationTranslator) =>
  createFirstStepSchema(t)
    .merge(createSecondStepSchema(t))
    .merge(ThirdStepSchema)
    .transform((data: any) => ({
      name: data.name,
      phone: data.phone,
      brand: data.brand,
      brandName: data.brandName,
      model: data.model,
      modelName: data.modelName,
      modelVersion: data.modelVersion,
      modelVersionName: data.modelVersionName,
      specification: data.specification,
      specificationName: data.specificationName,
      year: data.year,
      mileage: Number(data.mileage),
      tradeIn: data.tradeIn,
      description: data.description,
      images: (data.images?.filter((img: { url?: string }) => img?.url)?.map((img: { url: string }) => img.url) ??
        []) as string[],
    }));

export type SellCarFormSchemaType = z.infer<ReturnType<typeof createSellCarFormSchema>>;
